import {
    Box,
    Chip,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from '@mui/material'
import {
    SectorComercialGruposFilterDTOFields,
    SectorComercialGrupoViewDTO,
    SectorComercialGrupoViewDTOFields,
    SectorComercialViewDTO,
    SectorComercialViewDTOFields
} from './types'
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid'
import { RemoveCircleOutline, FilterAltOff, Search, GroupsOutlined } from '@mui/icons-material'
import { GridActionsCellItem } from '@mui/x-data-grid'
import { useEffect, useState } from 'react'
import { HttpSectorComercialABM } from './api'
import { EntityWithIdFields, EntityWithIdAndDescriptionFields, PaisLogoFields } from 'src/types'
import { Button } from 'src/components/forms'
import { z } from 'zod'
import { ControlledAutocomplete } from 'src/components/forms/ControlledAutocomplete'
import { Controller, useForm } from 'react-hook-form'
import { CountryAvatar } from '../../../../../components/icons/CountryAvatar'
import { usePaisLogo } from 'src/hooks/usePaisLogo'
import { HttpCacheWorkflow } from 'src/http/workflow/httpCacheWorkflow'
import toast from 'react-hot-toast'

export type EditSectorComercialProps = {
    sector: SectorComercialViewDTO
    onClose: () => void
}

const opcionAsignados = { value: 1, label: 'Asignados', color: 'success' as const }
const opcionTodos = { value: 2, label: 'Todos', color: 'info' as const }
const toggleOptions = [opcionAsignados, opcionTodos]

const schemaFiltroGrupos = z.object({
    nombre: z.string().optional(),
    codsPais: z.array(z.any()).optional(),
    codsMensajeTipo: z.array(z.any()).optional()
})

type SchemaFiltroGrupos = z.infer<typeof schemaFiltroGrupos>

function EditSectorComercial({ sector, onClose }: EditSectorComercialProps) {
    const { paisLogos } = usePaisLogo()
    const [groups, setGroups] = useState<SectorComercialGrupoViewDTO[]>()
    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>()
    const [activoFilter, setActivoFilter] = useState<number[]>([opcionAsignados.value])

    const { control, handleSubmit: handleSubmitFiltro, reset: resetForm } = useForm<SchemaFiltroGrupos>()

    const columnas: GridColDef<SectorComercialGrupoViewDTO>[] = [
        {
            headerName: 'Grupo',
            field: SectorComercialGrupoViewDTOFields.NombreGrupo,
            flex: 2
        },
        {
            headerName: 'Sistema',
            field: SectorComercialGrupoViewDTOFields.NombreSistema,
            flex: 3
        },
        {
            headerName: 'País',
            field: SectorComercialGrupoViewDTOFields.NombrePais,
            flex: 1,
            renderCell: ({ row: group }) => (
                <Chip
                    size='small'
                    label={group[SectorComercialGrupoViewDTOFields.NombrePais]}
                    avatar={<CountryAvatar countryCode={group[SectorComercialGrupoViewDTOFields.CodPais]} />}
                />
            )
        },
        {
            field: 'actions',
            type: 'actions',
            width: 60,
            getActions: ({ row: group }) => {
                const groupId = group[SectorComercialGrupoViewDTOFields.IdGrupo]
                const isAssigned = (rowSelectionModel ?? []).includes(groupId)

                if (!isAssigned) return []

                return [
                    <GridActionsCellItem
                        icon={
                            <Tooltip title='Quitar del sector'>
                                <RemoveCircleOutline fontSize='small' />
                            </Tooltip>
                        }
                        label='Quitar'
                        onClick={() => setRowSelectionModel(prev => (prev ?? []).filter(id => id !== groupId))}
                        color='error'
                    />
                ]
            }
        }
    ]

    const fetchGroups = (data?: SchemaFiltroGrupos) => {
        const filterParams: any = {}
        if (data?.codsPais?.length) {
            filterParams[SectorComercialGruposFilterDTOFields.CodsPais] = data.codsPais.map((c: any) => c.id ?? c.value)
        }
        if (data?.codsMensajeTipo?.length) {
            filterParams[SectorComercialGruposFilterDTOFields.CodsMensajeTipo] = data.codsMensajeTipo.map((c: any) => c.id ?? c.value)
        }
        if (data?.nombre) {
            filterParams.nombre = data.nombre
        }

        return HttpSectorComercialABM.getGrupos(sector[EntityWithIdFields.Id], filterParams).then(groups => {
            // Filtro client-side por asignados
            const soloAsignados = activoFilter.includes(opcionAsignados.value) && !activoFilter.includes(opcionTodos.value)
            const filtered = soloAsignados
                ? groups.filter(g => g[SectorComercialGrupoViewDTOFields.PerteneceAGrupo])
                : groups

            setGroups(filtered)
            const selectedGroups = groups
                .filter(group => group[SectorComercialGrupoViewDTOFields.PerteneceAGrupo])
                .map(group => group[SectorComercialGrupoViewDTOFields.IdGrupo])
            setRowSelectionModel(prev => [...new Set([...selectedGroups, ...(prev || [])])])
        })
    }

    useEffect(() => {
        handleSubmitFiltro(fetchGroups)()
    }, [])

    useEffect(() => {
        handleSubmitFiltro(fetchGroups)()
    }, [activoFilter])

    const onFilterChange = () => {
        handleSubmitFiltro(fetchGroups)()
    }

    const handleActivoChange = (_: any, newValue: number | null) => {
        if (newValue === null) return
        setActivoFilter([newValue])
    }

    const handleClearFilters = () => {
        resetForm({
            nombre: undefined,
            codsPais: undefined,
            codsMensajeTipo: undefined
        })
        setActivoFilter([opcionAsignados.value])
        onFilterChange()
    }

    const guardar = (cerrar: boolean) => {
        let mensajeError = 'No se pudieron guardar los grupos'

        const promise = HttpSectorComercialABM
            .actualizarGrupos(sector[EntityWithIdFields.Id], [...((rowSelectionModel as number[]) || [])])
            .then(() => {
                if (cerrar) {
                    setTimeout(onClose, 500)
                } else {
                    handleSubmitFiltro(fetchGroups)()
                }
            })
            .catch(err => {
                mensajeError = err?.message || mensajeError
                throw err
            })

        toast.promise(promise, {
            success: 'Grupos actualizados',
            error: () => mensajeError,
            loading: 'Guardando'
        })

        return promise
    }

    return (
        <>
            <DialogTitle sx={{ pb: 0 }}>
                <Stack direction='row' spacing={1.5} alignItems='center' justifyContent='space-between'>
                    <Stack direction='row' spacing={1.5} alignItems='center'>
                        <GroupsOutlined color='primary' />
                        <Typography variant='h6' fontWeight={600}>
                            Grupos de {sector[SectorComercialViewDTOFields.Nombre]}
                        </Typography>
                    </Stack>
                    <Stack direction='row' spacing={1}>
                        <Button variant='text' color='inherit' onClick={onClose}>
                            Cancelar
                        </Button>
                        <Button variant='outlined' color='primary' promise={() => guardar(false)}>
                            Guardar
                        </Button>
                        <Button variant='contained' color='primary' promise={() => guardar(true)}>
                            Guardar y cerrar
                        </Button>
                    </Stack>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ pt: '16px !important' }}>
                {/* Barra de filtros */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        alignItems: 'center',
                        mb: 2,
                        p: 2,
                        borderRadius: 1,
                        bgcolor: 'action.hover'
                    }}
                >
                    <Controller
                        control={control}
                        name='nombre'
                        render={({ field }) => (
                            <TextField
                                {...field}
                                value={field.value ?? ''}
                                label='Grupo'
                                size='small'
                                sx={{ minWidth: 160, flex: 1 }}
                                InputProps={{
                                    startAdornment: <Search fontSize='small' sx={{ mr: 0.5, color: 'text.secondary' }} />
                                }}
                            />
                        )}
                    />
                    <Box sx={{ minWidth: 160, flex: 1 }}>
                        <ControlledAutocomplete
                            control={control}
                            name={SectorComercialGruposFilterDTOFields.CodsPais}
                            label='País'
                            loadOptions={() =>
                                Promise.resolve(paisLogos).then(res =>
                                    res.map(s => ({
                                        id: s.id,
                                        descripcion: s[EntityWithIdAndDescriptionFields.Description]
                                    }))
                                )
                            }
                            multiple
                        />
                    </Box>
                    <Box sx={{ minWidth: 160, flex: 1 }}>
                        <ControlledAutocomplete
                            control={control}
                            name={SectorComercialGruposFilterDTOFields.CodsMensajeTipo}
                            label='Sistema'
                            loadOptions={() =>
                                HttpCacheWorkflow.getSistemasTipos().then(res =>
                                    res.map(s => ({
                                        id: s.id,
                                        descripcion: s.descripcion
                                    }))
                                )
                            }
                            multiple
                        />
                    </Box>
                    <ToggleButtonGroup
                        size='small'
                        exclusive
                        value={activoFilter[0] ?? null}
                        onChange={handleActivoChange}
                    >
                        {toggleOptions.map(option => (
                            <ToggleButton key={option.value} value={option.value} color={option.color}>
                                {option.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                    <Button variant='contained' size='small' onClick={onFilterChange}>
                        Buscar
                    </Button>
                    <Tooltip title='Limpiar filtros'>
                        <IconButton size='small' onClick={handleClearFilters}>
                            <FilterAltOff fontSize='small' />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Grilla de grupos */}
                <Box sx={{ height: '100%' }}>
                    <DataGrid
                        disableColumnFilter
                        disableColumnMenu
                        keepNonExistentRowsSelected
                        columns={columnas}
                        checkboxSelection
                        rowSelectionModel={rowSelectionModel}
                        onRowSelectionModelChange={setRowSelectionModel}
                        rows={groups ?? []}
                        getRowId={row => row[SectorComercialGrupoViewDTOFields.IdGrupo]}
                        density='compact'
                        pagination
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 25, page: 0 }
                            }
                        }}
                        pageSizeOptions={[25, 50, 75, 100]}
                    />
                </Box>
            </DialogContent>
        </>
    )
}

export default EditSectorComercial
